// src/components/messaging/MessageList.tsx
import React, { useRef, useEffect, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Message, GroupMessage, MessagingUser } from '../../types/messaging';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import MessageItem from './MessageItem';

interface MessageListProps {
  messages: (Message | GroupMessage)[];
  currentUserId: number;
  isGroup?: boolean;
  isLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => Promise<boolean>;
  onReply?: (message: Message | GroupMessage) => void;
  onEdit?: (message: Message | GroupMessage) => void;
  onDelete?: (message: Message | GroupMessage) => void;
  onReaction?: (message: Message | GroupMessage, reaction: string) => void;
  onRemoveReaction?: (message: Message | GroupMessage, reaction: string) => void;
  getSender?: (senderId: number) => MessagingUser | undefined;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  currentUserId,
  isGroup = false,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  onReply,
  onEdit,
  onDelete,
  onReaction,
  onRemoveReaction,
  getSender,
}) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const prevScrollHeightRef = useRef<number>(0);
  const isInitialLoadRef = useRef(true);

  // Format date separator
  const formatDateSeparator = (dateString: string): string => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return t('messaging.today', "Aujourd'hui");
    }
    if (isYesterday(date)) {
      return t('messaging.yesterday', 'Hier');
    }
    return format(date, 'd MMMM yyyy', { locale: fr });
  };

  // Check if we should show date separator
  const shouldShowDateSeparator = (
    currentMessage: Message | GroupMessage,
    previousMessage?: Message | GroupMessage
  ): boolean => {
    if (!previousMessage) return true;
    const currentDate = new Date(currentMessage.created_at);
    const previousDate = new Date(previousMessage.created_at);
    return !isSameDay(currentDate, previousDate);
  };

  // Check if we should show avatar (show if different sender or > 5 min gap)
  const shouldShowAvatar = (
    currentMessage: Message | GroupMessage,
    previousMessage?: Message | GroupMessage
  ): boolean => {
    if (!previousMessage) return true;
    if (currentMessage.sender_id !== previousMessage.sender_id) return true;
    const currentTime = new Date(currentMessage.created_at).getTime();
    const previousTime = new Date(previousMessage.created_at).getTime();
    return currentTime - previousTime > 5 * 60 * 1000; // 5 minutes
  };

  // Scroll to bottom
  const scrollToBottom = useCallback((smooth: boolean = false) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    if (isInitialLoadRef.current && messages.length > 0) {
      scrollToBottom();
      isInitialLoadRef.current = false;
      return;
    }

    // Check if user is near bottom
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      if (isNearBottom) {
        scrollToBottom(true);
      }
    }
  }, [messages.length, scrollToBottom]);

  // Reset initial load ref when conversation changes
  useEffect(() => {
    isInitialLoadRef.current = true;
  }, [messages.length > 0 ? ((messages[0] as Message).conversation_id || (messages[0] as GroupMessage).group_id) : null]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(async () => {
    if (!scrollContainerRef.current || isLoadingMore || !hasMore || !onLoadMore) return;

    const { scrollTop } = scrollContainerRef.current;

    // Load more when scrolled to top
    if (scrollTop < 100) {
      setIsLoadingMore(true);
      prevScrollHeightRef.current = scrollContainerRef.current.scrollHeight;

      try {
        await onLoadMore();

        // Maintain scroll position after loading
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            const newScrollHeight = scrollContainerRef.current.scrollHeight;
            const scrollDiff = newScrollHeight - prevScrollHeightRef.current;
            scrollContainerRef.current.scrollTop = scrollDiff;
          }
        });
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [isLoadingMore, hasMore, onLoadMore]);

  return (
    <Box
      ref={scrollContainerRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        py: 2,
        scrollBehavior: 'smooth',
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'grey.300',
          borderRadius: 3,
        },
      }}
    >
      {/* Loading more indicator */}
      {isLoadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Initial loading */}
      {isLoading && messages.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && messages.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2">
            {t('messaging.noMessages', 'Aucun message. Commencez la conversation!')}
          </Typography>
        </Box>
      )}

      {/* Messages */}
      {messages.map((message, index) => {
        const previousMessage = index > 0 ? messages[index - 1] : undefined;
        const showDateSeparator = shouldShowDateSeparator(message, previousMessage);
        const showAvatar = shouldShowAvatar(message, previousMessage);
        const isOwn = message.sender_id === currentUserId;
        const sender = getSender ? getSender(message.sender_id) : message.sender;

        return (
          <React.Fragment key={message.id}>
            {/* Date separator */}
            {showDateSeparator && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 2,
                  px: 4,
                }}
              >
                <Divider sx={{ flex: 1 }} />
                <Typography
                  variant="caption"
                  sx={{
                    px: 2,
                    color: 'text.secondary',
                    fontWeight: 500,
                    backgroundColor: 'background.paper',
                  }}
                >
                  {formatDateSeparator(message.created_at)}
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            )}

            {/* Message */}
            <MessageItem
              message={message}
              isOwn={isOwn}
              currentUserId={currentUserId}
              sender={sender}
              showAvatar={showAvatar && !isOwn}
              showName={isGroup && !isOwn && showAvatar}
              onReply={() => onReply?.(message)}
              onEdit={isOwn && message.type === 'text' ? () => onEdit?.(message) : undefined}
              onDelete={isOwn ? () => onDelete?.(message) : undefined}
              onReaction={(reaction) => onReaction?.(message, reaction)}
              onRemoveReaction={(reaction) => onRemoveReaction?.(message, reaction)}
            />
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default MessageList;
